import { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSellerOrdersSocket } from '../hooks/useSellerOrdersSocket';
import { SELLER_PENDING_CONFIRMED_COUNT_KEY } from '../hooks/useOrders';
import {
  showNewOrderToast,
  showOrderCanceledToast,
  showOrderDostavistaToast,
  showSellerInboxToast,
} from '../new-order-toast';

const UNREAD_QK = ['seller-notifications', 'unread-count'] as const;
const LIST_QK = ['seller-notifications', 'list'] as const;

type SellerNewOrderToastListenerProps = {
  /** Сокет (кабинет одобрен). */
  enabled?: boolean;
  /** Тосты по заказам (секция ORDERS). */
  ordersEnabled?: boolean;
};

const handledEventKeys = new Set<string>();

/** Чтобы поллинг колокольчика не дублировал тост сразу после WS. */
let lastInboxWsAt = 0;

export function markSellerInboxWsHandled() {
  lastInboxWsAt = Date.now();
}

export function wasSellerInboxWsRecent(ms = 4000): boolean {
  return Date.now() - lastInboxWsAt < ms;
}

function markHandled(key: string): boolean {
  if (handledEventKeys.has(key)) return false;
  handledEventKeys.add(key);
  window.setTimeout(() => handledEventKeys.delete(key), 30_000);
  return true;
}

function bumpPendingConfirmedCount(
  queryClient: ReturnType<typeof useQueryClient>,
  delta: number,
) {
  queryClient.setQueryData<{ count: number }>(
    SELLER_PENDING_CONFIRMED_COUNT_KEY,
    (prev) => ({ count: Math.max(0, (prev?.count ?? 0) + delta) }),
  );
}

function invalidateOrders(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({
    queryKey: SELLER_PENDING_CONFIRMED_COUNT_KEY,
  });
  void queryClient.invalidateQueries({ queryKey: ['orders'] });
}

/** Тосты по заказам + inbox-уведомлениям (WebSocket). */
export function SellerNewOrderToastListener({
  enabled = true,
  ordersEnabled = true,
}: SellerNewOrderToastListenerProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;
  const ordersEnabledRef = useRef(ordersEnabled);
  ordersEnabledRef.current = ordersEnabled;

  const isOnOrderPage = (orderId: number, orderNumber: string) => {
    const path = locationRef.current.pathname;
    return path === `/orders/${orderId}` || path === `/orders/${orderNumber}`;
  };

  useSellerOrdersSocket(
    {
      onNewOrder: (payload) => {
        if (!ordersEnabledRef.current) return;
        if (!markHandled(`new:${payload.orderId}`)) return;

        bumpPendingConfirmedCount(queryClient, 1);
        invalidateOrders(queryClient);

        if (isOnOrderPage(payload.orderId, payload.orderNumber)) return;

        showNewOrderToast({
          orderId: payload.orderId,
          orderNumber: payload.orderNumber,
          totalAmount: payload.totalAmount,
          onOpen: () => navigate(`/orders/${payload.orderId}`),
        });
      },
      onOrderCanceled: (payload) => {
        if (!ordersEnabledRef.current) return;
        if (!markHandled(`canceled:${payload.orderId}`)) return;

        bumpPendingConfirmedCount(queryClient, -1);
        invalidateOrders(queryClient);

        if (isOnOrderPage(payload.orderId, payload.orderNumber)) return;

        showOrderCanceledToast({
          orderId: payload.orderId,
          orderNumber: payload.orderNumber,
          onOpen: () => navigate(`/orders/${payload.orderId}`),
        });
      },
      onOrderDostavista: (payload) => {
        if (!ordersEnabledRef.current) return;
        if (!markHandled(`dv:${payload.kind}:${payload.orderId}`)) return;

        invalidateOrders(queryClient);

        if (isOnOrderPage(payload.orderId, payload.orderNumber)) return;

        showOrderDostavistaToast({
          orderId: payload.orderId,
          orderNumber: payload.orderNumber,
          kind: payload.kind,
          onOpen: () => navigate(`/orders/${payload.orderId}`),
        });
      },
      onNotificationNew: (payload) => {
        const key =
          payload.id > 0
            ? `inbox:${payload.id}`
            : `inbox:${payload.title}:${Date.now()}`;
        if (payload.id > 0 && !markHandled(key)) return;
        if (payload.id <= 0) markHandled(key);

        markSellerInboxWsHandled();
        void queryClient.invalidateQueries({ queryKey: UNREAD_QK });
        void queryClient.invalidateQueries({ queryKey: LIST_QK });

        // Уже есть отдельные sticky-тосты по заказам/Достависте
        const dataType =
          payload.data && typeof payload.data === 'object'
            ? (payload.data as { type?: unknown }).type
            : null;
        if (
          dataType === 'dostavista_order_canceled' ||
          dataType === 'dostavista_courier_search_again'
        ) {
          return;
        }

        showSellerInboxToast({
          id: payload.id,
          title: payload.title,
          body: payload.body,
        });
      },
    },
    enabled,
  );

  return null;
}
